import cx from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { FunctionComponent, useState } from 'react';

import Button from '../../ui/button';
import BPImage from '../../ui/v2/image';
import { useAtom } from 'jotai';
import { brandAtom, modelAtom, versionAtom } from '../../../hooks/use-picker-selection';
import { allSavedCarsAtom, selectVehicleStepAtom, vehiclePickerOpen } from '../../../state/vehicle';
import Chevrelon from '../../ui/icons/cheverlon';
import { useAppContext } from '../../providers/app-context';

const VehicleSelectionModal = dynamic(
  () => import('../../modules/vehicle-seletion-modal'),
  {
    ssr: false,
  }
);

type TIndetifyYourVehcile = {
  vehicle: {
    brand: string;
    model: string;
    version?: string;
    brand_alias?: string;
  };
};

const IndentifyYourVehicle: FunctionComponent<TIndetifyYourVehcile> = ({
  vehicle,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [afterFirstRenderOpen, setAfterFirstRenderOpen] = useState(false);
  const [, setBrandAtom] = useAtom(brandAtom);
  const [, setModelAtom] = useAtom(modelAtom);
  const [, setVersionAtom] = useAtom(versionAtom);
  const [, setVehiclePicker] = useAtom(vehiclePickerOpen);
  const [, setSelectedVehicleStep] = useAtom(selectVehicleStepAtom);
  const [savedVehicles] = useAtom(allSavedCarsAtom);
  const { isBparts, tenantConfig, brand } = useAppContext();

  const openVehiclePicker = () => {
    setBrandAtom(null);
    setModelAtom(null);
    setVersionAtom(null);

    if (savedVehicles && savedVehicles?.length) {
      setSelectedVehicleStep('SELECT_SAVED_CAR');
    } else {
      if (tenantConfig.id === 8 && brand) {
        setSelectedVehicleStep('SELECT_MODEL');
      } else {
        setSelectedVehicleStep('SELECT_BRAND');
      }
    }
    setVehiclePicker((prevValue) => !prevValue);
    return;
  };

  return (
    <div className='max-lg:px-4 flex flex-col items-center gap-2'>
      <h3 className={cx('text-[18px] lg:text-[32px]', 'font-bold')}>
        {t('unique-vehicle:is_this_your_vehicle')}
      </h3>
      <div className={cx('text-sm lg:text-base')}>
        {t('unique-vehicle:identify_your_vehicle')}
      </div>
      <div className={cx('text-[18px] lg:text-[32px] flex gap-4 items-center justify-center')}>
        <div className={cx(
          'relative',
          'aspect-video',
          'shrink-0',
          'flex h-[32px] w-[32px]',
        )}>
          <BPImage
            src={
              vehicle.brand_alias
                ? `${process.env.STATIC_PREFIX}/brands/logos/${vehicle.brand_alias}.svg`
                : `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
            }
            layout="fill"
            objectFit="contain"
            objectPosition="center"
            fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
            className={cx('bg-cover mix-blend-multiply')}
            alt={`${vehicle.brand_alias || 'car placeholder'
              } logo`}
          />
        </div>
        <span className={cx('text-[18px] lg:text-[32px]')}>
          <b>{vehicle.brand}</b>{` ${vehicle.model
            } ${vehicle?.version || ''}`}
        </span></div>
      <div className={cx('mt-4 lg:mt-6 inline-block')}>
        <Button
          variant="GHOST-PRIMARY"
          onClick={openVehiclePicker}
          className={cx(
            'flex flex-row gap-4 justify-between items-center hover:!bg-neutral-10',
            isBparts && '!text-blue-100'
          )}
        >
          {t('unique-vehicle:add_plate_or_brand')}
          <Chevrelon className="w-4 -rotate-90"></Chevrelon>
        </Button>
      </div>
      {afterFirstRenderOpen && (
        <VehicleSelectionModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default IndentifyYourVehicle;
